import React, { useMemo, useState } from 'react';
import { useAdminUsers } from '../../hooks/useAdminUsers.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const formatDate = (value) => {
  if (!value) {
    return '—';
  }
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString();
  } catch (error) {
    return value;
  }
};

const UsersSection = ({ onNotify }) => {
  const {
    items,
    total,
    page,
    perPage,
    loading,
    error,
    search,
    setSearch,
    setPage,
    refresh,
    toggleActive,
    createUser,
    updateUser,
    loadUserRestaurants,
    loadSyncPreview,
    syncUsers,
    loadPermissionsPreview,
    syncPermissions
  } = useAdminUsers();

  const [searchDraft, setSearchDraft] = useState(search);
  const [adminModal, setAdminModal] = useState({
    open: false,
    user: null,
    busy: false,
    values: { id_boh: '', is_super_admin: false },
    error: null
  });
  const [createModal, setCreateModal] = useState({
    open: false,
    busy: false,
    error: null,
    values: { name: '', email: '', id_boh: '' }
  });
  const [syncState, setSyncState] = useState({
    open: false,
    loading: false,
    error: null,
    items: [],
    counts: null,
    submitting: false,
    filter: ''
  });
  const [permissionsState, setPermissionsState] = useState({
    open: false,
    loading: false,
    error: null,
    items: [],
    counts: null,
    submitting: false,
    filter: ''
  });
  const [restaurantsState, setRestaurantsState] = useState({
    open: false,
    loading: false,
    error: null,
    user: null,
    items: [],
    filter: ''
  });

  useLucideIcons([
    items.length,
    adminModal.open,
    syncState.open,
    createModal.open,
    permissionsState.open,
    restaurantsState.open,
    restaurantsState.items.length
  ]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / perPage)), [total, perPage]);
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchDraft(value);
    setSearch(value.trim());
  };

  const handleSearchReset = () => {
    setSearchDraft('');
    setSearch('');
  };

  const handleOpenAdminModal = (user) => {
    setAdminModal({
      open: true,
      user,
      busy: false,
      error: null,
      values: {
        id_boh: user.id_boh ?? '',
        is_super_admin: Boolean(user.is_super_admin)
      }
    });
  };

  const handleToggleActive = async (user) => {
    const action = user.activo ? 'desactivar' : 'activar';
    const confirmed = window.confirm(`¿Deseas ${action} al usuario ${user.name || user.email}?`);
    if (!confirmed) {
      return;
    }
    try {
      const response = await toggleActive(user.id);
      onNotify?.({ type: 'success', message: response?.message || `Usuario ${action}do.` });
      refresh();
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || `No se pudo ${action} al usuario.` });
    }
  };

  const handleAdminChange = (field, value) => {
    setAdminModal((prev) => ({
      ...prev,
      values: { ...prev.values, [field]: value },
      error: null
    }));
  };

  const handleSaveAdmin = async () => {
    if (!adminModal.user) {
      return;
    }
    const user = adminModal.user;
    const idBohValue = typeof adminModal.values.id_boh === 'string'
      ? adminModal.values.id_boh.trim()
      : adminModal.values.id_boh;

    if (!idBohValue) {
      setAdminModal((prev) => ({ ...prev, error: 'El ID BoH es obligatorio.' }));
      return;
    }

    setAdminModal((prev) => ({ ...prev, busy: true }));
    try {
      const response = await updateUser(user.id, {
        id_boh: idBohValue,
        is_super_admin: adminModal.values.is_super_admin
      });
      onNotify?.({ type: 'success', message: response?.message || 'Usuario actualizado.' });
      setAdminModal({
        open: false,
        user: null,
        busy: false,
        values: { id_boh: '', is_super_admin: false },
        error: null
      });
      refresh();
    } catch (err) {
      setAdminModal((prev) => ({ ...prev, busy: false, error: err?.message || 'No se pudieron actualizar los privilegios.' }));
      onNotify?.({ type: 'error', message: err?.message || 'No se pudieron actualizar los privilegios.' });
    }
  };

  const closeAdminModal = () => {
    setAdminModal({
      open: false,
      user: null,
      busy: false,
      values: { id_boh: '', is_super_admin: false },
      error: null
    });
  };

  const handleOpenCreateModal = () => {
    setCreateModal({
      open: true,
      busy: false,
      error: null,
      values: { name: '', email: '', id_boh: '' }
    });
  };

  const closeCreateModal = () => {
    setCreateModal({
      open: false,
      busy: false,
      error: null,
      values: { name: '', email: '', id_boh: '' }
    });
  };

  const handleCreateChange = (field, value) => {
    setCreateModal((prev) => ({
      ...prev,
      values: { ...prev.values, [field]: value },
      error: null
    }));
  };

  const handleCreateUser = async () => {
    const name = createModal.values.name.trim();
    const email = createModal.values.email.trim().toLowerCase();
    const id_boh = typeof createModal.values.id_boh === 'string'
      ? createModal.values.id_boh.trim()
      : createModal.values.id_boh;

    if (!name || !email || !id_boh) {
      setCreateModal((prev) => ({ ...prev, error: 'Completa nombre, correo e ID BoH.' }));
      return;
    }

    setCreateModal((prev) => ({ ...prev, busy: true }));
    try {
      const response = await createUser({ name, email, id_boh });
      onNotify?.({ type: 'success', message: response?.message || 'Usuario creado.' });
      closeCreateModal();
      refresh();
    } catch (err) {
      setCreateModal((prev) => ({ ...prev, busy: false, error: err?.message || 'No se pudo crear el usuario.' }));
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo crear el usuario.' });
    }
  };

  const handleOpenSyncModal = async () => {
    setSyncState({
      open: true,
      loading: true,
      error: null,
      items: [],
      counts: null,
      submitting: false,
      filter: ''
    });
    try {
      const data = await loadSyncPreview();
      const normalized = (data?.new_users || []).map((user, index) => ({
        ...user,
        key: `${user.id_boh || user.email}-${index}`,
        selected: false
      }));
      setSyncState((prev) => ({
        ...prev,
        loading: false,
        items: normalized,
        counts: {
          total: data?.total_count ?? normalized.length,
          new: data?.new_count ?? normalized.length,
          existing: data?.existing_count ?? 0
        }
      }));
    } catch (err) {
      setSyncState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'No se pudo cargar la sincronización.'
      }));
    }
  };

  const updateSyncItem = (index, updates) => {
    setSyncState((prev) => ({
      ...prev,
      items: prev.items.map((item, current) => (current === index ? { ...item, ...updates } : item))
    }));
  };

  const toggleAllSyncUsers = (checked) => {
    setSyncState((prev) => ({
      ...prev,
      items: prev.items.map((item) => ({ ...item, selected: checked }))
    }));
  };

  const handleSyncFilterChange = (event) => {
    const value = event.target.value;
    setSyncState((prev) => ({ ...prev, filter: value }));
  };

  const filteredSyncItems = useMemo(() => {
    const query = syncState.filter.toLowerCase();
    return syncState.items
      .map((item, index) => ({ ...item, __index: index }))
      .filter((item) => {
        if (!query) {
          return true;
        }
        const name = item.name?.toLowerCase() || '';
        const email = item.email?.toLowerCase() || '';
        return name.includes(query) || email.includes(query);
      });
  }, [syncState.items, syncState.filter]);

  const handleSyncUsers = async () => {
    const selected = syncState.items.filter((item) => item.selected);
    if (!selected.length) {
      setSyncState((prev) => ({ ...prev, error: 'Selecciona al menos un usuario.' }));
      return;
    }
    setSyncState((prev) => ({ ...prev, submitting: true, error: null }));
    try {
      const payload = selected.map((item) => ({
        id_boh: item.id_boh,
        name: item.name,
        email: item.email
      }));
      const response = await syncUsers(payload);
      const message = [response?.message, (response?.errors || []).join(' ')].filter(Boolean).join(' ');
      onNotify?.({
        type: response?.errors?.length ? 'warning' : 'success',
        message: message || 'Sincronización completada.'
      });
      setSyncState({
        open: false,
        loading: false,
        error: null,
        items: [],
        counts: null,
        submitting: false,
        filter: ''
      });
      refresh();
    } catch (err) {
      setSyncState((prev) => ({ ...prev, submitting: false, error: err?.message || 'Error al sincronizar.' }));
    }
  };

  const closeSyncModal = () => {
    setSyncState({
      open: false,
      loading: false,
      error: null,
      items: [],
      counts: null,
      submitting: false,
      filter: ''
    });
  };

  const handleOpenPermissionsModal = async () => {
    setPermissionsState({
      open: true,
      loading: true,
      error: null,
      items: [],
      counts: null,
      submitting: false,
      filter: ''
    });
    try {
      const data = await loadPermissionsPreview();
      const normalized = (data?.new_assignments || []).map((item, index) => ({
        ...item,
        key: `${item.user_id}-${item.sucursal_id}-${index}`
      }));
      setPermissionsState((prev) => ({
        ...prev,
        loading: false,
        items: normalized,
        counts: {
          total: data?.total_count ?? normalized.length,
          new: data?.new_count ?? normalized.length,
          existing: data?.existing_count ?? 0
        }
      }));
    } catch (err) {
      setPermissionsState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'No se pudo cargar la sincronización de permisos.'
      }));
    }
  };

  const handlePermissionsFilterChange = (event) => {
    const value = event.target.value;
    setPermissionsState((prev) => ({ ...prev, filter: value }));
  };

  const filteredPermissions = useMemo(() => {
    const query = permissionsState.filter.toLowerCase();
    return permissionsState.items.filter((item) => {
      if (!query) {
        return true;
      }
      const name = item.user_name?.toLowerCase() || '';
      const email = item.user_email?.toLowerCase() || '';
      const sucursal = item.sucursal_nombre?.toLowerCase() || '';
      return name.includes(query) || email.includes(query) || sucursal.includes(query);
    });
  }, [permissionsState.items, permissionsState.filter]);

  const handleSyncPermissions = async () => {
    setPermissionsState((prev) => ({ ...prev, submitting: true, error: null }));
    try {
      const response = await syncPermissions();
      const message = [response?.message, (response?.errors || []).join(' ')].filter(Boolean).join(' ');
      onNotify?.({
        type: response?.errors?.length ? 'warning' : 'success',
        message: message || 'Sincronización de permisos completada.'
      });
      setPermissionsState({
        open: false,
        loading: false,
        error: null,
        items: [],
        counts: null,
        submitting: false,
        filter: ''
      });
      refresh();
    } catch (err) {
      setPermissionsState((prev) => ({ ...prev, submitting: false, error: err?.message || 'Error al sincronizar permisos.' }));
    }
  };

  const closePermissionsModal = () => {
    setPermissionsState({
      open: false,
      loading: false,
      error: null,
      items: [],
      counts: null,
      submitting: false,
      filter: ''
    });
  };

  const handleOpenRestaurantsModal = async (user) => {
    setRestaurantsState({
      open: true,
      loading: true,
      error: null,
      user,
      items: [],
      filter: ''
    });
    try {
      const data = await loadUserRestaurants(user.id);
      setRestaurantsState((prev) => ({
        ...prev,
        loading: false,
        items: data?.restaurantes || []
      }));
    } catch (err) {
      setRestaurantsState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'No se pudieron obtener los restaurantes del usuario.'
      }));
    }
  };

  const closeRestaurantsModal = () => {
    setRestaurantsState({
      open: false,
      loading: false,
      error: null,
      user: null,
      items: [],
      filter: ''
    });
  };

  const handleRestaurantsFilterChange = (event) => {
    const value = event.target.value;
    setRestaurantsState((prev) => ({ ...prev, filter: value }));
  };

  const filteredRestaurants = useMemo(() => {
    const query = restaurantsState.filter.toLowerCase().trim();
    if (!query) {
      return restaurantsState.items;
    }
    return restaurantsState.items.filter((item) => {
      const name = item.nombre?.toLowerCase() || '';
      const id = item.id_boh != null ? String(item.id_boh) : '';
      return name.includes(query) || id.includes(query);
    });
  }, [restaurantsState.items, restaurantsState.filter]);

  const renderRows = () => {
    if (!items.length) {
      return (
        <tr>
          <td colSpan={6} className="empty-state">
            <div className="empty-state-icon"><i data-lucide="users" /></div>
            <div className="empty-state-text">No hay usuarios registrados</div>
            <div className="empty-state-subtext">Los usuarios aparecerán cuando inicien sesión.</div>
          </td>
        </tr>
      );
    }

    return items.map((user) => (
      <tr key={user.id}>
        <td>
          <div className="user-info-cell">
            {user.picture_url ? (
              <img src={user.picture_url} alt="Avatar" className="user-avatar-table" />
            ) : (
              <div className="user-avatar-table placeholder-avatar">
                <i data-lucide="user" />
              </div>
            )}
            <div className="user-meta">
              <strong>{user.name || 'Sin nombre'}</strong>
            </div>
          </div>
        </td>
        <td className="user-email">{user.email}</td>
        <td className="user-id-boh">{user.id_boh ?? '—'}</td>
        <td>
          <span className={`role-badge ${user.is_super_admin ? 'role-super-admin' : 'role-user'}`}>
            <i data-lucide={user.is_super_admin ? 'crown' : 'user'} />
            {user.is_super_admin ? 'Super Admin' : 'Usuario'}
          </span>
        </td>
        <td>
          <span className={`status-badge ${user.activo ? 'status-active' : 'status-inactive'}`}>
            {user.activo ? 'Activo' : 'Inactivo'}
          </span>
        </td>
        <td>
          <div className="action-buttons">
            <button
              type="button"
              className="action-btn"
              title="Ver restaurantes asignados"
              onClick={() => handleOpenRestaurantsModal(user)}
            >
              <i data-lucide="store" />
            </button>
            <button
              type="button"
              className="action-btn edit"
              title="Gestionar Admin"
              onClick={() => handleOpenAdminModal(user)}
            >
              <i data-lucide="settings" />
            </button>
            <button
              type="button"
              className={`action-btn ${user.activo ? 'delete' : 'activate'}`}
              title={user.activo ? 'Desactivar' : 'Activar'}
              onClick={() => handleToggleActive(user)}
            >
              <i data-lucide={user.activo ? 'user-x' : 'user-check'} />
            </button>
          </div>
        </td>
      </tr>
    ));
  };

  return (
    <section id="users-section" className="admin-section active">
      <div className="section-header">
        <h2 className="section-title">Gestión de Usuarios</h2>
        <div className="section-actions">
          <form className="search-container" onSubmit={(event) => event.preventDefault()}>
            <span className="search-icon"><i data-lucide="search" /></span>
            <input
              type="text"
              className="form-input"
              placeholder="Buscar por nombre o email..."
              value={searchDraft}
              onChange={handleSearchChange}
            />
            {searchDraft ? (
              <button type="button" className="btn-secondary" onClick={handleSearchReset}>
                Limpiar
              </button>
            ) : null}
          </form>
          <button className="btn-secondary ghost-button" type="button" onClick={handleOpenCreateModal}>
            <span className="btn-icon"><i data-lucide="user-plus" /></span>
            Agregar usuario
          </button>
          <button className="btn-secondary" type="button" onClick={handleOpenPermissionsModal}>
            <span className="btn-icon"><i data-lucide="shield-check" /></span>
            Sincronizar permisos BoH
          </button>
          <button className="btn-primary" type="button" onClick={handleOpenSyncModal}>
            <span className="btn-icon"><i data-lucide="refresh-cw" /></span>
            Sincronizar Usuarios
          </button>
        </div>
      </div>
      <div className="data-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Cargando usuarios...</div>
          </div>
        ) : (
          <table className="data-table" id="users-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Correo</th>
                <th>ID BoH</th>
                <th>Rol</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr>
                  <td colSpan={6} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="alert-triangle" /></div>
                    <div className="empty-state-text">{error}</div>
                    <div className="empty-state-subtext">Intenta recargar o verifica tu conexión.</div>
                  </td>
                </tr>
              ) : renderRows()}
            </tbody>
          </table>
        )}
      </div>
      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        perPage={perPage}
        onPageChange={setPage}
      />
      {createModal.open ? (
        <AddUserModal
          state={createModal}
          onClose={closeCreateModal}
          onChange={handleCreateChange}
          onSubmit={handleCreateUser}
        />
      ) : null}
      {adminModal.open && adminModal.user ? (
        <UserAdminModal
          user={adminModal.user}
          busy={adminModal.busy}
          error={adminModal.error}
          values={adminModal.values}
          onChange={handleAdminChange}
          onClose={closeAdminModal}
          onSave={handleSaveAdmin}
        />
      ) : null}
      {syncState.open ? (
        <SyncUsersModal
          state={syncState}
          items={filteredSyncItems}
          onClose={closeSyncModal}
          onToggleAll={toggleAllSyncUsers}
          onUpdateItem={updateSyncItem}
          onFilterChange={handleSyncFilterChange}
          onSync={handleSyncUsers}
        />
      ) : null}
      {permissionsState.open ? (
        <SyncPermissionsModal
          state={permissionsState}
          items={filteredPermissions}
          onClose={closePermissionsModal}
          onFilterChange={handlePermissionsFilterChange}
          onSync={handleSyncPermissions}
        />
      ) : null}
      {restaurantsState.open ? (
        <UserRestaurantsModal
          state={restaurantsState}
          items={filteredRestaurants}
          onClose={closeRestaurantsModal}
          onFilterChange={handleRestaurantsFilterChange}
        />
      ) : null}
    </section>
  );
};

const Pagination = ({ page, totalPages, total, perPage, onPageChange }) => (
  <div className="pagination-container">
    <div className="pagination-info">
      <span>Página {page} de {totalPages}</span>
      <span className="pagination-total">
        {total.toLocaleString()} registros · {perPage} por página
      </span>
    </div>
    <div className="pagination-actions">
      <button
        type="button"
        className="pagination-btn"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        <i data-lucide="chevron-left" />
      </button>
      <button
        type="button"
        className="pagination-btn"
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
      >
        <i data-lucide="chevron-right" />
      </button>
    </div>
  </div>
);

const AddUserModal = ({ state, onClose, onChange, onSubmit }) => (
  <div className="modal" style={{ display: 'block' }}>
    <div className="modal-content user-admin-sheet">
      <div className="modal-header glossy">
        <div>
          <p className="modal-eyebrow">Nuevo acceso manual</p>
          <h3><i data-lucide="user-plus" /> Agregar usuario</h3>
          <p className="modal-subtitle">Crea un usuario con los campos mínimos para acceder al panel.</p>
        </div>
        <button className="modal-close" type="button" onClick={onClose} disabled={state.busy}>
          <i data-lucide="x" />
        </button>
      </div>
      <div className="modal-body form-grid">
        <div className="form-group">
          <label>Nombre completo</label>
          <input
            type="text"
            className="form-input"
            placeholder="Ej. Sam Taylor"
            value={state.values.name}
            onChange={(event) => onChange('name', event.target.value)}
            disabled={state.busy}
          />
        </div>
        <div className="form-group">
          <label>Correo</label>
          <input
            type="email"
            className="form-input"
            placeholder="correo@timhortonsmx.com"
            value={state.values.email}
            onChange={(event) => onChange('email', event.target.value)}
            disabled={state.busy}
          />
        </div>
        <div className="form-group">
          <label>ID BoH</label>
          <input
            type="number"
            className="form-input"
            placeholder="ID numérico"
            value={state.values.id_boh}
            onChange={(event) => onChange('id_boh', event.target.value)}
            disabled={state.busy}
          />
        </div>
        {state.error ? (
          <div className="form-error-banner full-width">
            <i data-lucide="alert-circle" /> {state.error}
          </div>
        ) : null}
      </div>
      <div className="modal-footer frosted-footer">
        <button className="btn-secondary" type="button" onClick={onClose} disabled={state.busy}>
          Cancelar
        </button>
        <button className="btn-primary" type="button" onClick={onSubmit} disabled={state.busy}>
          {state.busy ? 'Guardando…' : 'Crear usuario'}
        </button>
      </div>
    </div>
  </div>
);

const UserAdminModal = ({ user, values, busy, error, onClose, onChange, onSave }) => (
  <div className="modal" style={{ display: 'block' }}>
    <div className="modal-content user-admin-sheet">
      <div className="modal-header">
        <div>
          <p className="modal-eyebrow">Panel de control</p>
          <h3>Preferencias de usuario</h3>
          <p className="modal-subtitle">Ajusta privilegios y enlaza el ID BoH almacenado.</p>
        </div>
        <button className="modal-close" type="button" onClick={onClose} disabled={busy}>
          <i data-lucide="x" />
        </button>
      </div>
      <div className="modal-body user-admin-modal glossy-body">
        <div className="user-admin-header">
          {user.picture_url ? (
            <img src={user.picture_url} alt="Avatar" className="user-avatar-large" />
          ) : (
            <div className="user-avatar-large placeholder-avatar">
              <i data-lucide="user" />
            </div>
          )}
          <div className="user-identity">
            <div className="user-identity-top">
              <div>
                <h4>{user.name || 'Sin nombre'}</h4>
                <p>{user.email}</p>
              </div>
              <div className="user-identity-tags">
                <span className={`chip ${user.activo ? 'chip-success' : 'chip-muted'}`}>
                  {user.activo ? 'Activo' : 'Inactivo'}
                </span>
                <span className={`chip ${user.is_super_admin ? 'chip-gold' : 'chip-muted'}`}>
                  {user.is_super_admin ? 'Super Admin' : 'Usuario'}
                </span>
              </div>
            </div>
            <div className="user-admin-stats concise">
              <div className="user-stat">
                <span className="label">Último acceso</span>
                <strong>{formatDate(user.last_login_at)}</strong>
              </div>
              <div className="user-stat">
                <span className="label">Inicios de sesión</span>
                <strong>{user.login_count || 0}</strong>
              </div>
              <div className="user-stat">
                <span className="label">Registrado</span>
                <strong>{formatDate(user.created_at)}</strong>
              </div>
            </div>
          </div>
        </div>
        <div className="user-admin-controls">
          <div className="form-group">
            <label>ID BoH</label>
            <input
              type="number"
              className="form-input neumorphic"
              value={values.id_boh}
              onChange={(event) => onChange('id_boh', event.target.value)}
              disabled={busy}
            />
            <p className="input-hint">Debe coincidir con el ID registrado en BoH.</p>
          </div>
          <label className="checkbox-item pill-checkbox">
            <input
              type="checkbox"
              checked={values.is_super_admin}
              onChange={(event) => onChange('is_super_admin', event.target.checked)}
              disabled={busy}
            />
            <span className="checkmark" />
            <div className="checkbox-copy">
              <strong>Super admin</strong>
              <small>Acceso completo al panel, cambios críticos habilitados.</small>
            </div>
          </label>
        </div>
        {error ? (
          <div className="form-error-banner full-width">
            <i data-lucide="alert-circle" /> {error}
          </div>
        ) : null}
      </div>
      <div className="modal-footer frosted-footer">
        <button className="btn-secondary" type="button" onClick={onClose} disabled={busy}>
          Cancelar
        </button>
        <button className="btn-primary" type="button" onClick={onSave} disabled={busy}>
          {busy ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>
    </div>
  </div>
);

const SyncUsersModal = ({
  state,
  items,
  onClose,
  onToggleAll,
  onUpdateItem,
  onFilterChange,
  onSync
}) => {
  const selectedCount = state.items.filter((item) => item.selected).length;

  return (
    <div className="modal" style={{ display: 'block' }}>
      <div className="modal-content large-modal">
        <div className="modal-header">
          <h3>
            <i data-lucide="refresh-cw" />
            Sincronizar Usuarios con BoH
          </h3>
          <button className="modal-close" type="button" onClick={onClose}>
            <i data-lucide="x" />
          </button>
        </div>
        <div className="modal-body">
          {state.loading ? (
            <div className="loading-state">
              <div className="loading-spinner" />
              <div className="loading-text">Obteniendo usuarios desde BoH...</div>
            </div>
          ) : (
            <>
              <SyncSummary counts={state.counts} />
              {state.error ? (
                <div className="empty-state">
                  <div className="empty-state-icon"><i data-lucide="alert-triangle" /></div>
                  <div className="empty-state-text">{state.error}</div>
                </div>
              ) : (
                <>
                  <div className="sync-select-all-container">
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={state.items.length > 0 && selectedCount === state.items.length}
                        onChange={(event) => onToggleAll(event.target.checked)}
                      />
                      <span className="checkmark" />
                      Seleccionar todo
                    </label>
                    <div className="sync-filter">
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Filtrar por nombre o email..."
                        value={state.filter}
                        onChange={onFilterChange}
                      />
                    </div>
                    <span className="selected-count">{selectedCount} seleccionados</span>
                  </div>
                  <div className="data-table-container">
                    <table className="data-table" id="sync-users-boh-table">
                      <thead>
                        <tr>
                          <th />
                          <th>ID BoH</th>
                          <th>Nombre</th>
                          <th>Email</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="empty-state">
                              <div className="empty-state-icon"><i data-lucide="check-circle-2" /></div>
                              <div className="empty-state-text">No hay usuarios nuevos para sincronizar.</div>
                            </td>
                          </tr>
                        ) : (
                          items.map((user) => (
                            <tr key={user.key}>
                              <td>
                                <label className="checkbox-item">
                                  <input
                                    type="checkbox"
                                    checked={user.selected}
                                    onChange={(event) => onUpdateItem(user.__index, { selected: event.target.checked })}
                                  />
                                  <span className="checkmark" />
                                </label>
                              </td>
                              <td>{user.id_boh || '-'}</td>
                              <td><strong>{user.name || 'Sin nombre'}</strong></td>
                              <td>{user.email || 'Sin email'}</td>
                              <td><span className="status-badge status-inactive">Nuevo</span></td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" type="button" onClick={onClose} disabled={state.submitting}>
            Cancelar
          </button>
          <button
            className="btn-primary"
            type="button"
            disabled={state.submitting || selectedCount === 0 || Boolean(state.error)}
            onClick={onSync}
          >
            {state.submitting ? 'Sincronizando…' : `Sincronizar ${selectedCount > 0 ? `${selectedCount} usuarios` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

const UserRestaurantsModal = ({ state, items, onClose, onFilterChange }) => (
  <div className="modal" style={{ display: 'block' }}>
    <div className="modal-content large-modal">
      <div className="modal-header">
        <div>
          <h3>
            <i data-lucide="store" /> Restaurantes asignados
          </h3>
          <p className="modal-subtitle">
            {state.user?.name || state.user?.email ? (
              <>
                {state.user?.name || 'Sin nombre'} · {state.user?.email || 'Sin correo'}
              </>
            ) : null}
          </p>
        </div>
        <button className="modal-close" type="button" onClick={onClose}>
          <i data-lucide="x" />
        </button>
      </div>
      <div className="modal-body">
        {state.loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Cargando restaurantes...</div>
          </div>
        ) : state.error ? (
          <div className="empty-state">
            <div className="empty-state-icon"><i data-lucide="alert-triangle" /></div>
            <div className="empty-state-text">{state.error}</div>
          </div>
        ) : (
          <>
            <div className="sync-select-all-container">
              <div className="sync-filter" style={{ width: '100%' }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Filtrar por nombre o ID BoH..."
                  value={state.filter}
                  onChange={onFilterChange}
                />
              </div>
              <span className="selected-count">{items.length} resultados</span>
            </div>
            <div className="data-table-container">
              <table className="data-table" id="user-restaurants-table">
                <thead>
                  <tr>
                    <th>ID BoH</th>
                    <th>Restaurante</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="empty-state">
                        <div className="empty-state-icon"><i data-lucide="info" /></div>
                        <div className="empty-state-text">No hay restaurantes asignados.</div>
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => (
                      <tr key={item.id_boh ?? item.nombre}>
                        <td className="user-id-boh">{item.id_boh ?? '—'}</td>
                        <td><strong>{item.nombre || '—'}</strong></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      <div className="modal-footer">
        <button className="btn-secondary" type="button" onClick={onClose}>
          Cerrar
        </button>
      </div>
    </div>
  </div>
);

const SyncPermissionsModal = ({ state, items, onClose, onFilterChange, onSync }) => (
  <div className="modal" style={{ display: 'block' }}>
    <div className="modal-content large-modal">
      <div className="modal-header">
        <h3>
          <i data-lucide="shield-check" />
          Sincronizar permisos BoH
        </h3>
        <button className="modal-close" type="button" onClick={onClose} disabled={state.submitting}>
          <i data-lucide="x" />
        </button>
      </div>
      <div className="modal-body">
        {state.loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Buscando permisos en BoH...</div>
          </div>
        ) : (
          <>
            <PermissionsSummary counts={state.counts} />
            <p className="modal-subtitle">
              Esta acción sincroniza todas las asignaciones usuario-sucursal: agrega las nuevas y elimina las obsoletas.
            </p>
            {state.error ? (
              <div className="empty-state">
                <div className="empty-state-icon"><i data-lucide="alert-triangle" /></div>
                <div className="empty-state-text">{state.error}</div>
              </div>
            ) : (
              <>
                <div className="sync-filter">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Filtrar por usuario, correo o sucursal..."
                    value={state.filter}
                    onChange={onFilterChange}
                    disabled={state.submitting}
                  />
                </div>
                <div className="data-table-container">
                  <table className="data-table" id="sync-permissions-table">
                    <thead>
                      <tr>
                        <th>Usuario</th>
                        <th>Correo</th>
                        <th>Sucursal</th>
                        <th className="text-right">IDs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="empty-state">
                            <div className="empty-state-icon"><i data-lucide="check-circle-2" /></div>
                            <div className="empty-state-text">
                              No hay permisos nuevos. Puedes ejecutar la sincronización para depurar asignaciones obsoletas.
                            </div>
                          </td>
                        </tr>
                      ) : (
                        items.map((item) => (
                          <tr key={item.key}>
                            <td><strong>{item.user_name || 'Sin nombre'}</strong></td>
                            <td>{item.user_email || 'Sin correo'}</td>
                            <td>{item.sucursal_nombre || 'Sin sucursal'}</td>
                            <td className="text-right">
                              <small>U:{item.user_id} · S:{item.sucursal_id}</small>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
      <div className="modal-footer">
        <button className="btn-secondary" type="button" onClick={onClose} disabled={state.submitting}>
          Cancelar
        </button>
        <button
          className="btn-primary"
          type="button"
          disabled={state.submitting || state.loading}
          onClick={onSync}
        >
          {state.submitting ? 'Sincronizando…' : 'Ejecutar sincronización'}
        </button>
      </div>
    </div>
  </div>
);

const SyncSummary = ({ counts }) => {
  if (!counts) {
    return null;
  }
  return (
    <div className="sync-stats" id="sync-users-stats">
      <span className="stat-item">
        <i data-lucide="database" />
        <strong>{counts.total}</strong> en BoH
      </span>
      <span className="stat-item new">
        <i data-lucide="plus-circle" />
        <strong>{counts.new}</strong> nuevos
      </span>
      <span className="stat-item">
        <i data-lucide="check-circle" />
        <strong>{counts.existing}</strong> existentes
      </span>
    </div>
  );
};

const PermissionsSummary = ({ counts }) => {
  if (!counts) {
    return null;
  }
  return (
    <div className="sync-stats" id="sync-permissions-stats">
      <span className="stat-item">
        <i data-lucide="database" />
        <strong>{counts.total}</strong> permisos en BoH
      </span>
      <span className="stat-item new">
        <i data-lucide="plus-circle" />
        <strong>{counts.new}</strong> nuevos
      </span>
      <span className="stat-item">
        <i data-lucide="check-circle" />
        <strong>{counts.existing}</strong> existentes
      </span>
    </div>
  );
};

export default UsersSection;
